import styles from './MSHModal.module.css';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Button, CheckBox, Icon } from '@iwoca/orion';

import NameFields from './fields/NameFields';
import DOBFields from './fields/DOBFields';
import { TFormValues, TPerson } from './MSHForm.types';
import AddressFields from './fields/AddressFields';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import {
    PutStateRequestBody,
    fetchGetStateByStateKey,
    fetchPutState
} from '@iwoca/lapi-client/edge';
import { tracking } from '@iwoca/frontend-tracking-library';
import { FormattedMessage } from 'react-intl';
import messages from './MSHForm.messages';
import {
    convertDeserialisedPersonToFormValues,
    createCustomerStateWithNewOrUpdatedPerson
} from './MSHForm.helpers';

const MSHForm = ({
    onCancel,
    onSubmitSuccess,
    onSubmitError,
    personBeingEdited
}: {
    onCancel: () => void;
    onSubmitSuccess: () => void;
    onSubmitError: () => void;
    personBeingEdited: TPerson | undefined;
}) => {
    const defaultFormValues = personBeingEdited
        ? convertDeserialisedPersonToFormValues(personBeingEdited)
        : {
              address: { postcode: '' }
          };

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        getValues,
        setValue
    } = useForm<TFormValues>({
        defaultValues: defaultFormValues
    });

    const stateKey = useStateKey();

    const { data: customerState } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey })
    });

    const queryClient = useQueryClient();

    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        }
    });

    const onSubmit: SubmitHandler<TFormValues> = async (values) => {
        if (!customerState) return;

        // Create a payload with new person
        const newState = createCustomerStateWithNewOrUpdatedPerson(
            values,
            customerState
        );

        try {
            await saveCustomerState.mutateAsync(newState);
            tracking.ampli.drawdownMshPersonAdded();
            onSubmitSuccess();
        } catch (e) {
            console.error(e);
            onSubmitError();
        }
    };

    return (
        <div className={styles.formWrap}>
            <h1>
                <FormattedMessage {...messages.title} />
            </h1>

            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                <div>
                    {/* Name fields */}
                    <NameFields
                        errors={errors}
                        register={register}
                        disabled={Boolean(personBeingEdited)}
                    />

                    {/* DOB fields */}
                    <DOBFields
                        errors={errors}
                        register={register}
                        disabled={Boolean(personBeingEdited)}
                    />

                    {/* Roles checkboxes [Director/Shareholder/Guarantor] */}
                    <div className={`${styles.fields} ${styles.roleFields}`}>
                        <label className={styles.label}>
                            This person is a:
                        </label>
                        <CheckBox {...register('director')} label="Director" />
                        <CheckBox
                            {...register('shareholder')}
                            label="Shareholder"
                        />
                        <p className={styles.guarantorInfo}>
                            <Icon
                                icon="infoCircleOutline"
                                className={styles.guarantorInfoIcon}
                                height="20"
                                width="20"
                            />
                            <FormattedMessage {...messages.guarantorHint} />
                        </p>
                    </div>

                    {/* Address fields */}
                    <AddressFields
                        errors={errors}
                        register={register}
                        getValues={getValues}
                        setValue={setValue}
                    />
                </div>

                {/* Save/Cancel buttons */}
                <div className={styles.formButtonsContainer}>
                    <Button
                        variant="secondary-alt"
                        className={styles.ctaButton}
                        onClick={onCancel}
                    >
                        <FormattedMessage {...messages.cancelCTA} />
                    </Button>

                    <Button
                        variant="primary"
                        className={styles.ctaButton}
                        type="submit"
                        loading={isSubmitting}
                    >
                        <FormattedMessage {...messages.saveCTA} />
                    </Button>
                </div>
            </form>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default MSHForm;
